import type { ChangeEventHandler, FocusEventHandler } from 'react';
import { DatePicker, Select } from 'antd';
import dayjs from 'dayjs';
import LocationCascade from '../../../components/LocationCascade';
import TagsInput from '../../../components/TagsInput';
import PartyLocationDropdown from './PartyLocationDropdown';
import { Field, SectionHeading, fieldCls } from './formFields';
import PhoneRow from './PhoneRow';
import {
  DEPARTMENTS,
  GENDERS,
  RELIGIONS,
  RESERVATIONS,
  TITLES,
  toSelectOptions,
  type UserFormValues,
} from './types';

type SetField = (field: string, value: unknown) => void;

interface Props {
  values: UserFormValues;
  setFieldValue: SetField;
  handleChange: ChangeEventHandler<HTMLInputElement | HTMLTextAreaElement>;
  handleBlur: FocusEventHandler<HTMLInputElement | HTMLTextAreaElement>;
  errorOf: (name: keyof UserFormValues) => string | undefined;
  organization?: number;
  designationOptions: { value: string; label: string }[];
  departmentOptions: { value: string; label: string }[];
  /**
   * The selected department's own hierarchy (from field_options). When set, the location picker
   * loads that department's node tree (organisation/node/tree?kind=…) instead of the EC tree.
   */
  hierarchyKind?: string;
  savedCascade?: { label?: string } | null;
  onLocationRank: (rank?: number) => void;
}

/** Government Official (bureaucracy) create form — flat layout (Figma 1382-1104). */
export default function GovernmentOfficialForm({
  values,
  setFieldValue,
  handleChange,
  handleBlur,
  errorOf,
  organization,
  designationOptions,
  departmentOptions,
  hierarchyKind,
  savedCascade,
  onLocationRank,
}: Props) {
  return (
    <div className="flex flex-col gap-6">
      {/* Primary Information */}
      <div className="flex flex-col gap-4">
        <SectionHeading>Primary Information</SectionHeading>
        <div className="flex flex-col gap-3 md:flex-row">
          <div className="w-full md:w-[120px]">
            <Field label="Title">
              <Select
                className="w-full"
                placeholder="Shri"
                value={values.title || undefined}
                onChange={(v) => setFieldValue('title', v ?? '')}
                options={toSelectOptions(TITLES)}
                allowClear
              />
            </Field>
          </div>
          <Field label="First Name" htmlFor="firstName" required error={errorOf('firstName')}>
            <input
              id="firstName"
              name="firstName"
              placeholder="e.g. Rajesh"
              value={values.firstName}
              onChange={handleChange}
              onBlur={handleBlur}
              className={fieldCls(errorOf('firstName'))}
            />
          </Field>
          <Field label="Middle Name" htmlFor="middleName">
            <input
              id="middleName"
              name="middleName"
              placeholder="e.g. Kumar"
              value={values.middleName}
              onChange={handleChange}
              className={fieldCls()}
            />
          </Field>
          <Field label="Last Name" htmlFor="lastName" required error={errorOf('lastName')}>
            <input
              id="lastName"
              name="lastName"
              placeholder="e.g. Sharma"
              value={values.lastName}
              onChange={handleChange}
              onBlur={handleBlur}
              className={fieldCls(errorOf('lastName'))}
            />
          </Field>
        </div>

        <div className="flex flex-col gap-3 md:flex-row">
          <Field label="Email Address" htmlFor="email" error={errorOf('email')}>
            <input
              id="email"
              name="email"
              type="email"
              placeholder="rajesh.k@chunaav.gov.in"
              value={values.email}
              onChange={handleChange}
              onBlur={handleBlur}
              className={fieldCls(errorOf('email'))}
            />
          </Field>
          <Field label="Official / Station Office Address">
            <input
              name="officeAddress"
              placeholder="e.g. SDM Office, Complex Sector 4, Civil Lines"
              value={values.officeAddress}
              onChange={handleChange}
              className={fieldCls()}
            />
          </Field>
        </div>

        <PhoneRow
          contact={values.contact}
          landline={values.landline}
          landlineExt={values.landlineExt}
          whatsapp={values.whatsapp}
          whatsappIndex={values.whatsappIndex}
          contactError={errorOf('contact')}
          setFieldValue={setFieldValue}
        />
      </div>

      {/* Demographic Attributes + official assignment */}
      <div className="flex flex-col gap-4 border-t border-[#f1f5f9] pt-6">
        <SectionHeading>Demographic Attributes</SectionHeading>
        <div className="flex flex-col gap-3 md:flex-row">
          <Field label="Date of Birth" required error={errorOf('dob')}>
            <DatePicker
              className="w-full"
              format="DD/MM/YYYY"
              placeholder="dd/mm/yyyy"
              status={errorOf('dob') ? 'error' : undefined}
              value={values.dob ? dayjs(values.dob) : null}
              onChange={(d) => setFieldValue('dob', d ? d.format('YYYY-MM-DD') : '')}
              disabledDate={(d) => d && d.isAfter(dayjs(), 'day')}
            />
          </Field>
          <Field label="Gender" required error={errorOf('gender')}>
            <Select
              className="w-full"
              placeholder="Select Gender"
              status={errorOf('gender') ? 'error' : undefined}
              value={values.gender || undefined}
              onChange={(v) => setFieldValue('gender', v ?? '')}
              options={toSelectOptions(GENDERS)}
              allowClear
            />
          </Field>
          <Field label="Religion">
            <Select
              className="w-full"
              placeholder="Select Religion"
              value={values.religion || undefined}
              onChange={(v) => setFieldValue('religion', v ?? '')}
              options={toSelectOptions(RELIGIONS)}
              allowClear
            />
          </Field>
          <Field label="Caste / Sub-Caste">
            <input
              name="caste"
              placeholder="Community Name"
              value={values.caste}
              onChange={handleChange}
              className={fieldCls()}
            />
          </Field>
          <Field label="Reservation Category">
            <Select
              className="w-full"
              placeholder="General"
              value={values.reservation || undefined}
              onChange={(v) => setFieldValue('reservation', v ?? '')}
              options={toSelectOptions(RESERVATIONS)}
              allowClear
            />
          </Field>
        </div>

        <div className="flex flex-col gap-3 md:flex-row">
          <Field label="Department">
            <Select
              className="w-full"
              placeholder="Select Department"
              showSearch
              optionFilterProp="label"
              value={values.department[0] || undefined}
              // Designation + location are scoped to the department, so reset both on change.
              onChange={(v) => {
                setFieldValue('department', v ? [v] : []);
                setFieldValue('designationValue', '');
                setFieldValue('location', '');
                onLocationRank(undefined);
              }}
              options={departmentOptions.length ? departmentOptions : toSelectOptions(DEPARTMENTS)}
              allowClear
            />
          </Field>
          <Field label="Cadre / Designation">
            <Select
              className="w-full"
              placeholder="Select Cadre / Designation"
              showSearch
              optionFilterProp="label"
              value={values.designationValue || undefined}
              onChange={(v) => setFieldValue('designationValue', v ?? '')}
              options={designationOptions}
              allowClear
            />
          </Field>
        </div>

        <div className="flex flex-col gap-3 md:flex-row">
          <Field
            label="Designated Administrative Location / Jurisdiction"
            error={errorOf('location')}
          >
            {hierarchyKind ? (
              // The department has its own hierarchy — pick from its node tree.
              <PartyLocationDropdown
                organization={organization}
                value={values.location}
                kind={hierarchyKind}
                placeholder="Select Jurisdiction"
                invalid={Boolean(errorOf('location'))}
                onChange={(key) => {
                  setFieldValue('location', key);
                  onLocationRank(undefined);
                }}
              />
            ) : (
              <LocationCascade
                organization={organization}
                value={values.location}
                source="ec-tree"
                placeholder="Select Jurisdiction"
                invalid={Boolean(errorOf('location'))}
                initialLabel={savedCascade?.label}
                onChange={(sel) => {
                  setFieldValue('location', sel ? String(sel.value) : '');
                  onLocationRank(sel?.levelRank);
                }}
              />
            )}
          </Field>
          <Field label="Any Additional Roles">
            <TagsInput
              placeholder="Type a role and press Enter"
              value={values.additionalRoles}
              onChange={(v) => setFieldValue('additionalRoles', v)}
            />
          </Field>
        </div>
      </div>
    </div>
  );
}
